import React from 'react';
import { Routes, Route, Navigate, useLocation, useParams, useSearchParams, useNavigate } from 'react-router';
import CacheBuster from 'react-cache-buster';
import { setIsOpenChangePassword, setIsOpenFailedPayment, setIsOpenSuccessfulPayment } from 'store/slices/windowStateSlice';
import { useAppDispatch } from 'store/hook';
import Login from 'routes/Login/Login';
import SignUp from 'routes/SignUp/SignUp';
import Main from 'routes/Main/Main';
import Documents from 'routes/Documents/Documents';
import Sides from 'routes/Sides/Sides';
import Logs from 'routes/Logs/Logs'
import NDASection from 'routes/NDASection/NDASection';
import MainPageEmpty from 'routes/MainPageEmpty/MainPageEmpty';
import Chat from 'routes/Chat/Chat';
import Rooms from 'routes/Rooms/Rooms';
import Confirm from 'routes/Confirm/Confirm';
import Report from 'routes/Report/Report';
import ResetPassword from 'routes/PasswordInputes/ResetPassword';
import TempLink from 'routes/TempLink/TempLink';
import Mobile from 'routes/Mobile/Mobile';
import OrganizationBook from 'routes/OrganizationBook/OrganizationBook';
import TFAuth from 'routes/TFAuth/TFAuth';
import Billing from 'routes/Billing/Billing'
import WrongAddress from 'routes/WrongAddress/WrongAddress';
import FAQ from 'routes/FAQ/FAQ';
import AwaitingPayment from 'routes/AwaitingPayment/AwaitingPayment';
import TariffEnded from 'routes/TariffEnded/TariffEnded';
import OTPTFAuth from 'routes/TFAuth/OTPTFAuth';
import { BuildNames } from 'interfaces/IBuildLanguage';
import Reset from './routes/PasswordInputes/Reset';
import Users from 'routes/Users/Users';
import ConfirmRegistration from 'routes/ConfirmRegistration/ConfirmRegistration';
import Analytics from 'routes/Analytics/Analytics';
import RecycleBin from 'routes/RecycleBin/RecycleBin';
import VkAuth from 'routes/VkAuth/VkAuth';
import VkRegister from 'routes/VkAuth/VkRegister';
import YaAuth from 'routes/YaAuth/YaAuth';
import YaRegister from 'routes/YaAuth/YaRegister';
import TariffsComponent from 'components/TariffsComponent/TariffsComponent';
import DetailsComponent from 'components/DetailsComponent/DetailsComponent';
import { getKeyFromLocation } from 'helpers/pagesAndLocation';
import { getCookie } from 'helpers/cookies';
import { setFaviconAndTitle } from 'helpers/setFavicon';
import packageFile from '../package.json'
import './scss/app.scss';
import BlitzAuth from 'routes/BlitzAuth/BlitzAuth';
import AuthRoute from 'routes/AuthRoute/AuthRoute';

export default function App() {  
  const isBillingActive = window.REACT_APP_IS_BILLING_ACTIVE !== 'REPLACE_REACT_APP_IS_BILLING_ACTIVE' ? window.REACT_APP_IS_BILLING_ACTIVE : process.env.REACT_APP_IS_BILLING_ACTIVE;
  const buildName = window.REACT_APP_BUILD_NAME !== 'REPLACE_REACT_APP_BUILD_NAME' ? window.REACT_APP_BUILD_NAME : process.env.REACT_APP_BUILD_NAME;

  const isProduction = true;
  const navigate = useNavigate();
  
  React.useEffect(() => {    
    setFaviconAndTitle(buildName);
    const location = window.location.pathname;
    const isTempLink = location.includes('temp_link');
    const isMobile = /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(navigator.userAgent)
    if (isMobile && !isTempLink) navigate('/unsupported');
  }, []);

  return (
    <CacheBuster
      currentVersion={packageFile.version}
      isEnabled={isProduction}
      isVerboseMode={false}
      metaFileDirectory={'.'}
    >
      <Routes>
        <Route path='/login' element={<Authorization><Login /></Authorization>}/>
        <Route path='/blitz-auth' element={<Authorization><BlitzAuth /></Authorization>}/>
        <Route path='/external_auth' element={<Authorization><AuthRoute /></Authorization>}/>
        <Route path='/vk-auth' element={<Authorization><VkAuth /></Authorization>}/>
        <Route path='/ya-auth' element={<Authorization><YaAuth /></Authorization>}/>
        <Route path='/vk-register' element={<Authorization><VkRegister /></Authorization>}/>
        <Route path='/ya-register' element={<Authorization><YaRegister /></Authorization>}/>
        <Route path='/signup' element={<Authorization><SignUp /></Authorization>}/>
        <Route path='/confirm-registration' element={<Authorization><ConfirmRegistration /></Authorization>}/>
        <Route path='/tfa/:email' element={<Authorization><OTPTFAuth /></Authorization>}/>
        <Route path='/reset' element={<Authorization><Reset /></Authorization>}/>
        <Route path='/invite/:hash' element={<Authorization><SignUp /></Authorization>}/>
        <Route path='/users/register/accept/:user_id' element={<Authorization><Confirm /></Authorization>}/>
        <Route path='/users/restore-password/' element={<Authorization><ResetPassword /></Authorization>}/>
        <Route path='/temp_link/:document_token' element={<TempLink />}/>
        <Route path='/two_factor_authentication/:password' element={<TFAuth />}/>
        <Route path='/organization/book/:encoded_jwt' element={<OrganizationBook />} />
        <Route path='/unsupported' element={<Mobile />} />
        <Route path='/ref' element={<FAQ />} />
        <Route path='/' element={<Protected><Main /></Protected>}>
          <Route path='/billing' element={<Protected><Billing /></Protected>} />
          <Route path='/rooms' element={<Protected><Rooms /></Protected>} />
          <Route path='/report' element={<Protected><Report /></Protected>} />
          <Route path='/data-analytics' element={<Protected><Analytics /></Protected>} />
          <Route path='/noAvailablesRooms' element={<Protected><MainPageEmpty /></Protected>} />
          {
            isBillingActive === 'true'
              && <Route path='/tariffs' element={<Protected><TariffsComponent isProtected={true} /></Protected>}/>
          }
          <Route path='/global-chats'>
            <Route index element={<Protected><Chat /></Protected>}  />
            <Route path=':chatId' element={<Protected><Chat /></Protected>} />
              <Route path='support/:chatId' element={<Protected><Chat /></Protected>} />
          </Route>
          <Route path='/room/:room'>
            <Route index path='endedTariff' element={<Protected><TariffEnded/></Protected>} />
            <Route index path='awaitingPayment' element={<Protected><AwaitingPayment /></Protected>} />
            <Route index path='documents' element={<Protected><Documents/></Protected>} />
            <Route index path='documents/:folderId' element={<Protected><Documents/></Protected>} />
            <Route path='sides' element={<Protected><Sides /></Protected>}/>
            <Route path='logs' element={<Protected><Logs /></Protected>}/>
            <Route path='users' element={<Protected><Users /></Protected>}/>
            <Route path='nda' element={<Protected><NDASection /></Protected>}/>
            <Route path='recycleBin' element={<Protected><RecycleBin /></Protected>}/>
            <Route path='document' element={<Protected><DetailsComponent /></Protected>}/>
            <Route path='chat'>
              <Route index element={<Protected><Chat /></Protected>} />
              <Route path='document/:chatId' element={<Protected><DetailsComponent /></Protected>} />
              <Route path='user/:chatId' element={<Protected><Chat /></Protected>} />
              <Route path='telegram/:chatId' element={<Protected><Chat /></Protected>} />
            </Route>
          </Route>
        </Route>

        <Route path='*' element={<WrongAddress />}/>
      </Routes>
    </CacheBuster>
  );
}

const Protected = ({ children }: any) => {
  const authType = window.REACT_APP_AUTH_TYPE !== 'REPLACE_REACT_APP_AUTH_TYPE' ? window.REACT_APP_AUTH_TYPE : process.env.REACT_APP_AUTH_TYPE; 
  const accessToken = getCookie('athena-token');

  const { pathname: location } = useLocation();
  const params = useParams();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  
  if (searchParams.get('message') === 'successful_payment') dispatch(setIsOpenSuccessfulPayment(true));
  if (searchParams.get('message') === 'failed_payment') dispatch(setIsOpenFailedPayment(true));
  if (searchParams.get('message') === 'recreate') dispatch(setIsOpenFailedPayment(true));

  const documentId = searchParams.get('document_id');
  const modalSection = searchParams.get('modalSection');
  const webviewer_file_id = searchParams.get('webviewer_file_id'); // previous logic

  if (documentId) localStorage.setItem('document_id', documentId);
  if (modalSection) localStorage.setItem('modalSection', modalSection);
  if (webviewer_file_id) {
    localStorage.setItem('webviewer_file_id', webviewer_file_id);
    localStorage.setItem('document_id', webviewer_file_id)
  };
  
  if (params.room) {    
    const correctPathWithChatChildren = getKeyFromLocation(location, params.room);    
    params.room && localStorage.setItem('previousRoomId', params.room);
    if (params.folderId) {
      localStorage.setItem('previousFolderId', params.folderId);
    } else {      
      localStorage.removeItem('previousFolderId');      
      localStorage.removeItem('previousFolderPath');
    }
    params.chatId && localStorage.setItem('previousChatId', params.chatId);
    localStorage.setItem('previousPage', correctPathWithChatChildren);
  } else {
    const sectionOfRoom = location.split('/').filter(str => str !== '')[0];
    sectionOfRoom && localStorage.setItem('previousPage', sectionOfRoom);
    params.chatId && localStorage.setItem('previousChatId', params.chatId);
  };

  if (!accessToken) {    
    if (authType === 'blitz') {
      return <Navigate to='/blitz-auth' state={{ from: location }} />
    } else {
      return <Navigate to='/login' state={{ from: location }} />
    }
  }

  return children;
}

const Authorization = ({ children }: any) => {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { hash } = useParams();  
  
  const link_id = searchParams.get('link_id') || '';
  const userEmail = searchParams.get('email') || '';
  const user_idBasex64 = searchParams.get('user') || '';
  const user_id = atob(user_idBasex64);

  if (hash) {
    localStorage.setItem('invite_hash', hash);
  }

  if (link_id && userEmail && user_id) {
    localStorage.setItem('link_id_rp', link_id);
    localStorage.setItem('userEmail_rp', userEmail);
    localStorage.setItem('user_id_rp', user_id);
    dispatch(setIsOpenChangePassword(true));
  };

  if (getCookie('athena-token')) {
    return <Navigate to='/'/>
  }
  return children;
}

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext;
    REACT_APP_SERVER_API: string;
    REACT_APP_CONVERTER_API: string;
    REACT_APP_SIGNATURE_API: string;
    REACT_APP_BILLING_API: string;
    REACT_APP_BILLING_URL: string;
    REACT_APP_WS_API: string;
    REACT_APP_CONTACT_US: string;
    REACT_APP_IS_BILLING_ACTIVE: string;
    REACT_APP_BUILD_NAME: BuildNames | string;
    REACT_APP_BUILD_TYPE: string;
    REACT_APP_IOS_LINK_TAZA: string;
    REACT_APP_GOOGLE_LINK_TAZA: string;
    REACT_APP_APK_LINK_TAZA: string;
    REACT_APP_RUSTORE_LINK_TAZA: string;
    REACT_APP_IOS_LINK_ATHENA: string;
    REACT_APP_GOOGLE_LINK_ATHENA: string;
    REACT_APP_APK_LINK_ATHENA: string;
    REACT_APP_RUSTORE_LINK_ATHENA: string;
    REACT_APP_STORAGE_TYPE: string;
    REACT_APP_PAYMENT_TYPE: string;
    REACT_APP_IS_EDITING_ACTIVE: string;
    REACT_APP_ANALYTICS_URL: string;
    REACT_APP_AI_GENERATION_URL: string;
    REACT_APP_OCR_API: string;
    REACT_APP_VK_CLIENT_ID: string;
    REACT_APP_YA_CLIENT_ID: string;
    REACT_APP_YA_CLIENT_SECRET: string;
    REACT_APP_TEMP_MAIN_VIEWER: string;
    REACT_APP_MESSAGE_LENGTH: string;
    REACT_APP_AUTH_TYPE: string;
    REACT_APP_EXTERNAL_AUTH: string;
  }
}
